import { useEffect, useRef, useState } from "react";
import { haptic, type Outcome, type OutcomeKind } from "@karrtopelka/game-kit";
import { colors } from "@karrtopelka/ui";

/**
 * Stake-style wheel: a ring of segment blocks. A FIXED pointer at the top points down;
 * the RING spins (several turns, Power4-style ease-out over 3–5s) and the winning
 * segment settles under the pointer.
 *
 * Casino pass: the CURRENT STREAK sits in the hollow center — the stake physically
 * rides the wheel. Decelerating haptic ticks sell the final clacks, a green/gold flash
 * + burst ring celebrates a multiplier, a red flash mourns a bust.
 *
 * The outcome is decided SERVER-SIDE — the ring just stops a segment of the matching
 * kind under the pointer. Segments are distributed by the real odds so the ring tells
 * the gamble (lots of BUST). reduced-motion snaps to the result.
 */

// 30 even segments roughly matching the flat odds (bust 25 / +1 50 / x2 14 / nothing 5 /
// jackpot 4 / again 2). Counts: +1×14, bust×8, x2×4, nothing×2, jackpot×1, again×1.
// Interleaved so colors scatter around the ring.
const LAYOUT: OutcomeKind[] = [
  "+1", "bust", "+1", "x2", "+1", "bust",
  "+1", "nothing", "+1", "x2", "+1", "bust",
  "+1", "jackpot", "+1", "bust", "+1", "x2",
  "+1", "bust", "+1", "again", "+1", "x2",
  "+1", "bust", "+1", "nothing", "bust", "bust",
];
const N = LAYOUT.length;
const SEG = 360 / N;

const KIND_COLOR: Record<OutcomeKind, string> = {
  "+1": "#2f2f3a",
  x2: colors.accent,
  again: colors.accentMuted,
  bust: "#7a1230",
  nothing: "#46464f",
  jackpot: colors.gold,
};
const KIND_LABEL: Record<OutcomeKind, string> = {
  "+1": "+1",
  x2: "x2",
  again: "↻",
  bust: "✕",
  nothing: "–",
  jackpot: "x3",
};
const KIND_TEXT: Record<OutcomeKind, string> = {
  "+1": colors.textMuted,
  x2: colors.bg,
  again: colors.text,
  bust: "#ffd0dc",
  nothing: colors.textMuted,
  jackpot: colors.bg,
};

const WEDGES_BY_KIND: Record<OutcomeKind, number[]> = LAYOUT.reduce(
  (acc, kind, i) => {
    (acc[kind] ||= []).push(i);
    return acc;
  },
  {} as Record<OutcomeKind, number[]>,
);

const VIEW = 200;
const C = VIEW / 2;
const R_OUT = 94; // ring outer
const R_IN = 74; // ring inner (hollow center)

function reducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/** Annular segment path for block i (a chunk of the ring). */
function segPath(i: number): string {
  const pad = 1.2; // gap between blocks (deg)
  const a0 = ((i * SEG + pad - 90) * Math.PI) / 180;
  const a1 = (((i + 1) * SEG - pad - 90) * Math.PI) / 180;
  const ox0 = C + R_OUT * Math.cos(a0);
  const oy0 = C + R_OUT * Math.sin(a0);
  const ox1 = C + R_OUT * Math.cos(a1);
  const oy1 = C + R_OUT * Math.sin(a1);
  const ix1 = C + R_IN * Math.cos(a1);
  const iy1 = C + R_IN * Math.sin(a1);
  const ix0 = C + R_IN * Math.cos(a0);
  const iy0 = C + R_IN * Math.sin(a0);
  return `M ${ox0} ${oy0} A ${R_OUT} ${R_OUT} 0 0 1 ${ox1} ${oy1} L ${ix1} ${iy1} A ${R_IN} ${R_IN} 0 0 0 ${ix0} ${iy0} Z`;
}

export function Gauge({
  spinning,
  outcomes,
  streak,
  onLanded,
}: {
  spinning: boolean;
  outcomes: Outcome[] | null;
  /** Current run streak — displayed in the hollow center (the stake on the wheel). */
  streak: number;
  onLanded: (outcomes: Outcome[]) => void;
}) {
  const [rotation, setRotation] = useState(0);
  const [dur, setDur] = useState(4);
  const [landedLabel, setLandedLabel] = useState<string | null>(null);
  const [landedWedge, setLandedWedge] = useState<number | null>(null);
  const [landedKind, setLandedKind] = useState<OutcomeKind | null>(null);

  const idx = useRef(0);
  const rot = useRef(0); // accumulated ring rotation (forward)
  const animating = useRef(false);
  const tickTimers = useRef<number[]>([]);

  const clearTicks = () => {
    for (const t of tickTimers.current) clearTimeout(t);
    tickTimers.current = [];
  };
  useEffect(() => clearTicks, []);

  // Rotate the RING so segment w's center lands at the TOP (under the fixed pointer).
  const targetRotation = (kind: OutcomeKind, turns: number): { rot: number; w: number } => {
    const matches = WEDGES_BY_KIND[kind] ?? WEDGES_BY_KIND["+1"]!;
    const w = matches[Math.floor(Math.random() * matches.length)]!;
    const center = w * SEG + SEG / 2;
    const jitter = (Math.random() - 0.5) * (SEG * 0.5); // small, stays in the block
    const base = rot.current;
    const want = (((-center - jitter) % 360) + 360) % 360;
    const settle = (((want - (base % 360)) % 360) + 360) % 360;
    return { rot: base + turns * 360 + settle, w };
  };

  const winWedge = useRef(0); // terminal target wedge; highlighted only AFTER it stops

  const runStep = (chain: Outcome[], i: number) => {
    const o = chain[i]!;
    const terminal = i === chain.length - 1;
    const turns = terminal ? 4 + Math.floor(Math.random() * 4) : 2; // 4..7 turns terminal
    const ms = terminal ? 3000 + Math.floor(Math.random() * 2000) : 1100; // random 3–5s
    const { rot: next, w } = targetRotation(o.kind, turns);
    rot.current = next;
    if (terminal) winWedge.current = w;
    setDur(ms / 1000);
    setLandedLabel(null);
    setLandedWedge(null); // no highlight while spinning — only reveal on stop
    setLandedKind(null);

    // Decelerating clacks on the terminal spin — the ease-out means the last few
    // ticks stretch apart, which is where the tension lives.
    clearTicks();
    if (terminal) {
      for (const f of [0.3, 0.45, 0.58, 0.7, 0.8, 0.88, 0.94, 0.985]) {
        tickTimers.current.push(
          window.setTimeout(() => haptic.tap(), Math.round(ms * f)),
        );
      }
    }
    requestAnimationFrame(() => setRotation(next));
  };

  useEffect(() => {
    if (!spinning || !outcomes || outcomes.length === 0 || animating.current) return;
    animating.current = true;
    idx.current = 0;

    if (reducedMotion()) {
      const term = outcomes[outcomes.length - 1]!;
      const { rot: next, w } = targetRotation(term.kind, 0);
      rot.current = next;
      setDur(0);
      setRotation(next);
      setLandedWedge(w);
      setLandedLabel(term.label);
      setLandedKind(term.kind);
      animating.current = false;
      haptic.land();
      onLanded(outcomes);
      return;
    }
    runStep(outcomes, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinning, outcomes]);

  const handleEnd = () => {
    if (!animating.current || !outcomes) return;
    const last = idx.current === outcomes.length - 1;
    if (last) {
      animating.current = false;
      clearTicks();
      const kind = outcomes[idx.current]!.kind;
      setLandedWedge(winWedge.current); // reveal the highlight now that it's stopped
      setLandedLabel(outcomes[idx.current]!.label);
      setLandedKind(kind);
      if (kind === "bust") haptic.tooSoon();
      else if (kind === "x2" || kind === "jackpot") haptic.newBest();
      else haptic.land();
      onLanded(outcomes);
      return;
    }
    haptic.tap();
    idx.current += 1;
    runStep(outcomes, idx.current);
  };

  const won = landedKind === "x2" || landedKind === "jackpot";
  const busted = landedKind === "bust";

  return (
    <div className="relative flex aspect-square w-full max-w-[320px] items-center justify-center lg:max-w-[420px]">
      {/* one-beat felt flash on the verdict */}
      {won && <div className="pointer-events-none absolute inset-0 animate-flash-win rounded-full" />}
      {busted && <div className="pointer-events-none absolute inset-0 animate-flash-bust rounded-full" />}
      {/* gold burst ring on a multiplier */}
      {won && (
        <div className="pointer-events-none absolute inset-6 animate-burst rounded-full border-2 border-gold" />
      )}

      <svg viewBox={`0 0 ${VIEW} ${VIEW}`} className="h-full w-full">
        {/* outer + inner ring frame */}
        <circle cx={C} cy={C} r={R_OUT + 3} fill={colors.surface} stroke={colors.border} strokeWidth={2} />
        <circle cx={C} cy={C} r={R_IN - 2} fill={colors.bg} stroke={colors.border} strokeWidth={1} />

        {/* marquee bulbs on the cabinet frame: faint pink at rest, a chasing wave
            while the ring spins, gold strobe on a multiplier, red on a bust */}
        {LAYOUT.map((_, i) => {
          const a = ((i * SEG - 90) * Math.PI) / 180;
          const bx = C + (R_OUT + 3) * Math.cos(a);
          const by = C + (R_OUT + 3) * Math.sin(a);
          const fill = won ? colors.gold : busted ? colors.danger : colors.accent;
          return (
            <circle
              key={`bulb-${i}`}
              cx={bx}
              cy={by}
              r={1.3}
              fill={fill}
              className={spinning || won ? "animate-breathe" : undefined}
              style={
                spinning || won
                  ? { animationDelay: `-${((i / N) * 1.4).toFixed(2)}s` }
                  : { opacity: busted ? 0.85 : 0.3 }
              }
            />
          );
        })}

        {/* spinning ring of segment blocks */}
        <g
          onTransitionEnd={handleEnd}
          style={{
            transform: `rotate(${rotation}deg)`,
            transformOrigin: `${C}px ${C}px`,
            transformBox: "view-box",
            transition: dur > 0 ? `transform ${dur}s cubic-bezier(0.12,0.7,0.18,1)` : "none",
          }}
        >
          {LAYOUT.map((kind, i) => {
            const mid = ((i * SEG + SEG / 2 - 90) * Math.PI) / 180;
            const lr = (R_OUT + R_IN) / 2;
            const lx = C + lr * Math.cos(mid);
            const ly = C + lr * Math.sin(mid);
            const isWin = landedWedge === i;
            const winGlow =
              isWin && landedKind
                ? landedKind === "bust"
                  ? colors.danger
                  : landedKind === "jackpot"
                    ? colors.gold
                    : colors.text
                : null;
            return (
              <g key={i}>
                <path
                  d={segPath(i)}
                  fill={KIND_COLOR[kind]}
                  stroke={winGlow ?? "transparent"}
                  strokeWidth={isWin ? 2 : 0}
                  style={winGlow ? { filter: `drop-shadow(0 0 7px ${winGlow})` } : undefined}
                  opacity={landedWedge !== null && !isWin ? 0.45 : 1}
                />
                <text
                  x={lx}
                  y={ly}
                  fill={KIND_TEXT[kind]}
                  fontSize={6}
                  fontWeight={700}
                  textAnchor="middle"
                  dominantBaseline="central"
                  transform={`rotate(${i * SEG + SEG / 2} ${lx} ${ly})`}
                  opacity={landedWedge !== null && !isWin ? 0.5 : 1}
                >
                  {KIND_LABEL[kind]}
                </text>
              </g>
            );
          })}
        </g>

        {/* fixed pointer at top, points DOWN into the ring */}
        <polygon
          points={`${C - 7},2 ${C + 7},2 ${C},20`}
          fill={colors.accent}
          stroke={colors.bg}
          strokeWidth={1}
          style={{ filter: `drop-shadow(0 2px 4px rgba(0,0,0,0.6))` }}
        />
        <circle cx={C} cy={6} r={4} fill={colors.accent} stroke={colors.bg} strokeWidth={1} />
      </svg>

      {/* the stake, riding the wheel: current streak in the hollow center */}
      <div className="pointer-events-none absolute flex flex-col items-center">
        {landedLabel ? (
          <span
            className={`animate-pop rounded-full px-4 py-1.5 text-sm font-bold uppercase tracking-wide lg:px-5 lg:text-base ${
              busted
                ? "bg-danger/15 text-danger"
                : won
                  ? "bg-gold/15 text-gold"
                  : "bg-surface-raised text-accent"
            }`}
          >
            {landedLabel}
          </span>
        ) : (
          <>
            <span
              className={`font-display text-5xl font-extrabold tabular-nums lg:text-7xl ${
                spinning ? "animate-breathe text-text" : streak > 0 ? "text-accent" : "text-text-faint"
              }`}
            >
              {streak}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-text-faint lg:text-xs">
              {spinning ? "on the line" : "streak"}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
